/**
 * dtEventFormat — THE human line for a dt_events row (Epic C stream).
 *
 * Shared by NotificationBell and DashboardPage (their switches were exact
 * duplicates within a week of being written). The server keeps its own
 * describeEvent in server/utils/dtEvents.js — email/WhatsApp wording is a
 * different surface and deliberately not coupled to the UI copy.
 *
 * i18n: takes the `t` translator so the line renders in the active language;
 * dynamic status/priority values are translated through the dtStatus key maps.
 */

import { CALL_STATUS_LABEL_KEYS, PRIORITY_LABEL_KEYS } from '../constants/dtStatus';

export interface DtEventLike {
  type: string;
  payload: Record<string, any>;
}

export interface DescribeDtEventOptions {
  /** Append "(priority)" to call.created lines (dashboard style). */
  createdPriority?: boolean;
  /** Append the comment excerpt to call.commented lines (bell style). */
  commentText?: boolean;
  /** Use the active UI language for dates, rather than the browser's language. */
  locale?: string;
}

type TFn = (key: string, fallback?: string) => string;

/** Compact event labels for delivery history, where ticket/device details are not supplied. */
export const describeDtEventType = (type: string, t: TFn): string => {
  const keys: Record<string, string> = {
    'call.created': 'dt_notif_newTicket',
    'call.updated': 'dt_int_ev_updated',
    'call.commented': 'dt_notif_commentOn',
    'call.resolved': 'dt_notif_resolved',
    'call.sla_breach': 'dt_notif_overdue',
    'call.sla_escalation': 'dt_notif_needsAttention',
    'iot.alert': 'dt_notif_iotAlert',
    'iot.offline': 'dt_notif_deviceSilent',
  };
  return t(keys[type] || 'dt_notif_activityUpdate');
};

export const describeDtEvent = (e: DtEventLike, t: TFn, opts: DescribeDtEventOptions = {}): string => {
  const p = e.payload || {};
  const title = p.title ? `“${p.title}”` : '';
  const dated = (text: string, value: unknown, labelKey: string): string => {
    if (typeof value !== 'string' && typeof value !== 'number') return text.trim();
    const date = new Date(value);
    if (!Number.isFinite(date.getTime())) return text.trim();
    return `${text.trim()} · ${t(labelKey)} ${date.toLocaleString(opts.locale)}`;
  };
  switch (e.type) {
    case 'call.created': {
      const prio = (PRIORITY_LABEL_KEYS as Record<string, string>)[p.priority] || PRIORITY_LABEL_KEYS.normal;
      return `${t('dt_notif_newTicket')} ${title}${opts.createdPriority ? ` (${t(prio)})` : ''}`;
    }
    case 'call.updated': {
      const changes = p.changes || {};
      const parts: string[] = [];
      if (changes.status) parts.push(t((CALL_STATUS_LABEL_KEYS as Record<string, string>)[changes.status.to] || 'dt_notif_updated'));
      if (changes.priority) parts.push(t((PRIORITY_LABEL_KEYS as Record<string, string>)[changes.priority.to] || 'dt_priority_normal'));
      if (changes.assignee) parts.push(changes.assignee.to ? t('dt_notif_reassigned') : t('dt_notif_unassigned'));
      return `${t('dt_notif_ticket')}${title ? ` ${title}` : ''}: ${parts.join(', ') || t('dt_notif_updated')}`;
    }
    case 'call.commented':
      return `${t('dt_notif_commentOn')} ${title}${opts.commentText && p.text ? `: ${p.text}` : ''}`;
    case 'call.resolved':
      return `${t('dt_notif_resolved')} ${title}`.trim();
    case 'call.sla_breach':
      return dated(`${t('dt_notif_overdue')} ${title}`, p.deadline, 'dt_notif_dueAt');
    case 'call.sla_escalation':
      return dated(`${t('dt_notif_needsAttention')} ${title}`, p.deadline, 'dt_notif_attentionSince');
    case 'iot.offline':
      return dated(`${t('dt_notif_deviceSilent')}${p.deviceName ? `: ${p.deviceName}` : ''}`,
        p.lastReadingAt, 'dt_notif_lastReading');
    case 'iot.alert':
      return `${t('dt_notif_iotAlert')}: ${p.message || p.deviceName || t('dt_devstatus_alert')}`;
    default:
      return `${t('dt_notif_activityUpdate')}${title ? `: ${title}` : ''}`;
  }
};
